import { BuyerCustomerDTO, InvoiceDTO, InvoiceParamsFromPosDTO, Permission, Res, SaleDTO } from 'core/api';
import { Command, Cordova } from 'pos/common';
import { Result, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import * as InvoiceStore from './InvoiceStore';
import { InvoiceFacade } from 'pos/facades';
import { showCloseDocumentScreen, Invoice } from '../helpers/document-creation';
import { ensureCanRefund } from './Preconditions';
import { getRefundPaymentsMode } from './getRefundPaymentsMode';
import { ReverseExternalPaymentService } from 'pos/services';
import { canRefundOriginalPayments } from '../helpers/payments-processing/canRefundOriginalPayments';
import { Message } from 'core/ui-next/modal';

declare global {
	interface CommandType {
		createInvoiceRefund: (invoice: InvoiceDTO) => Promise<Result>
	}
}

Command.register('createInvoiceRefund', Preconditions.ensure(Permission.Ticket_Refund), async (invoice: InvoiceDTO): Promise<Result> => {

	const canRefund = await ensureCanRefund(invoice);
	if (!canRefund)
		return Result.error();

	const result = await getRefundPaymentsMode();
	if (!result.ok)
		return Result.error();

	return result.value === 'OriginalPayments' ? refundWithOriginalPayments(invoice) : refundWithNewPaymentsPayments(invoice);
});

async function refundWithNewPaymentsPayments(invoice: InvoiceDTO): Promise<Result<SaleDTO>> {

	const originalPayments = await InvoiceFacade.getOriginalPaymentsToRefund(invoice.Id);
	if (originalPayments.some(x => x.CardTransaction.TransactionId) && !SessionProvider.hasPermission(Permission.CardProcessor_AllowUnreferencedRefunds)) {
		await Message.show(Res.Msg.MSG_CANNOT_REFUND_INVOICE_WITH_CARD_PAYMENTS_WITHOUT_UNREFERENCED_REFUNDS_PERMISSION());
		return Result.error();
	}

	const ticketGlobalId = await InvoiceFacade.getTicketGlobalIdByInvoiceId(invoice.Id);
	return showCloseDocumentScreen({
		layout: 'RefundInvoice',
		getDocument: () => {
			return {
				invoiceId: invoice.Id,
				invoiceType: invoice.Type,
				posId: invoice.PosId,
				customer: invoice.Customer,
				pendingAmount: invoice.NetAmount,
				ticketGlobalId
			};
		},
		setCustomer: async (customer: BuyerCustomerDTO) => {
			await InvoiceStore.setCustomer(invoice.Id, customer);
			invoice = await InvoiceFacade.getById(invoice.Id);
		},
		closeDocument: (params: InvoiceParamsFromPosDTO) => InvoiceStore.refund(invoice.Id, params)
	});
}

async function refundWithOriginalPayments(invoice: InvoiceDTO): Promise<Result<SaleDTO>> {

	const ticketGlobalId = await InvoiceFacade.getTicketGlobalIdByInvoiceId(invoice.Id);
	const originalPayments = await InvoiceFacade.getOriginalPaymentsToRefund(invoice.Id);

	const { canRefund, reason } = canRefundOriginalPayments(originalPayments, SessionProvider.getCurrent().CardProcessorType, Cordova.isInitialized());
	if (!canRefund) {
		await Message.show(reason);
		return Result.error();
	}

	const service = ReverseExternalPaymentService.for('Refund', ticketGlobalId, originalPayments);
	const result = await service.refund();
	if (!result.ok)
		return Result.error();

	const refundResult = await Invoice.refund({
		invoiceType: invoice.Type,
		setCustomer: async () => { },
		refund: (params: InvoiceParamsFromPosDTO) => InvoiceStore.refund(invoice.Id, params),
		getDocument: () => {
			return {
				invoiceId: invoice.Id,
				invoiceType: invoice.Type,
				posId: invoice.PosId,
				customer: invoice.Customer,
				pendingAmount: invoice.NetAmount,
				ticketGlobalId
			};
		},
		commitCashPayments: async () => Result.ok(),
		payments: result.value
	});

	// Aquí estamos muy jodidos, porque hemos devuelto un pago que ya no se puede volver a devolver pero no lo hemos podido guardar en Ágora. Intentamos
	// recuperar el pago. OJO el pago original va a quedar devuelto, aquí sólo le cogemos el dinero. Esto es similar a lo que hacemos con el ExternalPaymentService
	// cuando cerramos un documento y falla por lo que sea. Sólo queda rastro en la auditoría.
	if (!refundResult.ok)
		await service.undoAll();

	return refundResult;

}

